"use client"

import React, { useState, useRef, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import HubspotForm from "./HubspotForm"

interface Option {
  label: string
  value: string
}

interface CustomSelectProps {
  label: string
  required?: boolean
  value: string
  onChange: (val: string) => void
  options: Option[]
}

const SERVICIO_OPTIONS: Option[] = [
  { value: "Campamentos Educativos en el extranjero", label: "Campamentos Educativos en el extranjero" },
  { value: "Cursos de Idiomas", label: "Cursos de Idiomas" },
  { value: "Estudios en el extranjero", label: "Estudios en el extranjero" },
  { value: "Turismo y Viajes", label: "Turismo y Viajes" },
  { value: "Visas y Trámites Migratorios", label: "Visas y Trámites Migratorios" },
]

const SUCURSAL_OPTIONS: Option[] = [
  { value: "Toluca", label: "Toluca" },
  { value: "Metepec", label: "Metepec" },
  { value: "Guadalajara", label: "Guadalajara" },
  { value: "Canadá", label: "Canadá" },
  { value: "Atención Online", label: "Atención Online (En línea)" },
]

function CustomSelect({
  label,
  required = true,
  value,
  onChange,
  options,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handlePointerDown(e: MouseEvent | TouchEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handlePointerDown)
      document.addEventListener("touchstart", handlePointerDown)
    }
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
    }
  }, [isOpen])

  const selectedOption = options.find((o) => o.value === value)

  return (
    <div className="w-full relative" ref={containerRef}>
      {/* Etiqueta exacta: Montserrat 16px, color #000000, peso regular */}
      <label
        className="block text-[16px] font-normal text-[#000000] mb-2"
        style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
      >
        {label}
        {required && <span className="text-[#e51520] ml-0.5">*</span>}
      </label>

      {/* Campo exacto: borde 1px continuo #999999, radio 9px, fondo blanco, texto Montserrat 16px #000000 con px-[16px] */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="w-full h-11 bg-white border border-[#999999] rounded-[9px] px-[16px] flex items-center justify-between text-[16px] text-[#000000] font-normal outline-none transition-colors cursor-pointer text-left hover:border-[#666666] focus:border-[#000000] focus:outline-none"
        style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
      >
        <span className="truncate pr-3">{selectedOption ? selectedOption.label : value}</span>
        <svg
          className={`w-4 h-4 text-[#666666] shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Menú desplegable con mismo radio 9px, borde #999999 y tipografía Montserrat */}
      {isOpen && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] bg-white border border-[#999999] rounded-[9px] shadow-xl z-50 max-h-60 overflow-y-auto py-1"
          style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value)
                  setIsOpen(false)
                }}
                className={`w-full px-[16px] py-2.5 text-left text-[15px] flex items-center justify-between transition-colors ${
                  isSelected
                    ? "bg-gray-100 font-medium text-[#000000]"
                    : "text-[#000000] hover:bg-gray-50"
                }`}
                style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg className="w-4 h-4 text-[#e51520] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface RegistrationModalProps {
  children: React.ReactNode
}

export function RegistrationModal({ children }: RegistrationModalProps) {
  const [servicio, setServicio] = useState("Campamentos Educativos en el extranjero")
  const [sucursal, setSucursal] = useState("Toluca")

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      {/* 
        Modal responsivo con tipografía Montserrat y estilos sincronizados con CRM HubSpot
      */}
      <DialogContent className="sm:max-w-[600px] w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-xl">
        <DialogHeader className="mb-2">
          <DialogTitle
            className="text-2xl font-black text-center text-gray-900"
            style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
          >
            Planeemos tu próxima gran experiencia
          </DialogTitle>
          <DialogDescription
            className="text-center text-gray-600 text-sm mt-1 max-w-md mx-auto"
            style={{ fontFamily: "var(--font-montserrat), 'Montserrat', sans-serif" }}
          >
            Recibe asesoría personalizada hoy mismo y conéctate al instante por WhatsApp con un asesor experto de Grupo LET.
          </DialogDescription>
        </DialogHeader>

        {/* Selectores estilizados idénticos a los campos del formulario oficial de HubSpot */}
        <div className="w-full space-y-4 mt-2 mb-0 px-0.5">
          {/* Campo 1: Servicio de Interés */}
          <CustomSelect
            label="¿En qué servicio estás interesado?"
            required={true}
            value={servicio}
            onChange={setServicio}
            options={SERVICIO_OPTIONS}
          />

          {/* Campo 2: Sucursal o Modalidad */}
          <CustomSelect
            label="¿En qué sucursal o modalidad prefieres tu atención?"
            required={true}
            value={sucursal}
            onChange={setSucursal}
            options={SUCURSAL_OPTIONS}
          />
        </div>
        
        {/* Formulario de contacto oficial de HubSpot */}
        <HubspotForm servicio={servicio} sucursal={sucursal} />
        
      </DialogContent>
    </Dialog>
  )
}
